import { useState } from "react";
import { ChecklistStep } from "../../components/widgets/ProgressBar";
import ChoiceGroup from "../../components/forms/ChoiceGroup";
import CheckboxGroup from "../../components/forms/CheckboxGroup";
import SelectInput from "../../components/forms/SelectInput";
import SliderInput from "../../components/forms/SliderInput";
import NotesField from "../../components/forms/NotesField";
import { useStore } from "../../js/state/store";

export default function BuyerHomeType() {
  const saved = useStore().state.buyer.current.preferences.homeType || {};
  const [form, setForm] = useState({
    homeTypes: saved.homeTypes || (saved.type ? [saved.type] : []),
    age: saved.age || "",
    floorplan: saved.floorplan || [],
    sqftMin: saved.sqftMin ?? 1200,
    sqftMax: saved.sqftMax ?? 2800,
    lotSize: saved.lotSize || "",
    terrain: saved.terrain || [],
    notes: saved.notes || "",
  });

  function update(event) {
    const { name, value, type } = event.target;
    setForm((current) => ({
      ...current,
      [name]: type === "range" ? Number(value) : value,
    }));
  }

  return (
    <ChecklistStep
      title="Home type"
      lead="Which kinds of homes should your agent put in front of you — and how should they feel to live in?"
      preferenceKey="homeType"
      value={form}
    >
      <section className="checklist-section">
        <h2>Dwelling Type</h2>
        <CheckboxGroup
          label="Home types to consider"
          name="homeTypes"
          values={form.homeTypes}
          onChange={update}
          layout="grid"
          options={[
            { value: "single-family", label: "Single-family" },
            { value: "condo", label: "Condo" },
            { value: "townhome", label: "Townhome" },
            { value: "manufactured", label: "Manufactured / modular" },
            { value: "new-construction", label: "New construction" },
          ]}
        />
        <SelectInput
          label="Age of home"
          name="age"
          value={form.age}
          onChange={update}
          options={[
            { value: "0-5", label: "0–5 years (newer)" },
            { value: "6-20", label: "6–20 years" },
            { value: "21-40", label: "21–40 years" },
            { value: "40+", label: "40+ years" },
            { value: "historic", label: "Historic character is a plus" },
            { value: "any", label: "No age preference" },
          ]}
        />
      </section>

      <section className="checklist-section">
        <h2>Floor Plan Style</h2>
        <ChoiceGroup
          label="Select all that work"
          name="floorplan"
          multiple
          values={form.floorplan}
          onChange={update}
          options={[
            { value: "single-story", label: "Single-story" },
            { value: "two-story", label: "Two-story" },
            { value: "split-level", label: "Split-level" },
            { value: "open-concept", label: "Open concept" },
            { value: "traditional", label: "Traditional / defined rooms" },
          ]}
        />
      </section>

      <section className="checklist-section">
        <h2>Size</h2>
        <SliderInput
          label="Minimum living area"
          name="sqftMin"
          min={600}
          max={6000}
          step={100}
          value={form.sqftMin}
          onChange={update}
          formatValue={(value) => `${Number(value).toLocaleString()} sq ft`}
        />
        <SliderInput
          label="Maximum living area"
          name="sqftMax"
          min={600}
          max={6000}
          step={100}
          value={form.sqftMax}
          onChange={update}
          formatValue={(value) => `${Number(value).toLocaleString()} sq ft`}
        />
        <SelectInput
          label="Lot size range"
          name="lotSize"
          value={form.lotSize}
          onChange={update}
          options={[
            { value: "small", label: "Under 5,000 sq ft" },
            { value: "standard", label: "5,000–10,000 sq ft" },
            { value: "quarter", label: "About ¼ acre" },
            { value: "half", label: "About ½ acre" },
            { value: "acre-plus", label: "1 acre or more" },
            { value: "any", label: "No lot-size preference" },
          ]}
        />
      </section>

      <section className="checklist-section">
        <h2>Terrain</h2>
        <CheckboxGroup
          label="Land / setting you want"
          name="terrain"
          values={form.terrain}
          onChange={update}
          layout="grid"
          options={[
            { value: "flat", label: "Flat" },
            { value: "hillside", label: "Hillside" },
            { value: "mountain", label: "Mountain" },
            { value: "coastal", label: "Coastal" },
          ]}
        />
        <NotesField
          label="Home type notes for my agent"
          name="notes"
          value={form.notes}
          onChange={update}
          placeholder="Fixer vs. turnkey, HOA condos vs. detached, ADU plans, and anything else about the dwelling itself."
        />
      </section>
    </ChecklistStep>
  );
}
